import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { logger } from "@/lib/logger";
import { trackEvent } from "@/lib/analytics";
import { supabase } from "@/integrations/supabase/client";
import { cookieUtils } from "@/utils/cookieUtils";

/**
 * NavigationOptimizer handles performance monitoring and prefetching strategies
 * to ensure smooth transitions and track loading times.
 * It also prevents infinite redirect loops and normalizes URLs.
 */
export const NavigationOptimizer = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // 0. URL Normalization Redirects & Loop Prevention
  useEffect(() => {
    const { pathname, search, hash } = window.location;
    
    // Recursive protection: if we've redirected more than 5 times in 10 seconds, stop.
    const now = Date.now();
    let loopData = { count: 0, last: 0 };
    try {
      loopData = JSON.parse(sessionStorage.getItem('nav_redirect_loop_check') || '{"count":0, "last":0}');
    } catch (e) {}
    
    if (now - loopData.last < 10000 && loopData.count > 5) {
      logger.error(`[Navigation] Potential infinite redirect loop detected for ${pathname}. Stopping redirects.`);
      
      const eventData = {
        action: 'infinite_redirect_loop_blocked',
        category: 'error',
        label: pathname,
        count: loopData.count
      };
      
      trackEvent(eventData);

      // Log to database for the dashboard
      supabase.from('portal_analytics_events').insert({
        event_type: 'navigation_error',
        page_path: pathname,
        anonymous_id: cookieUtils.get('scooterp_anon_id') || 'unknown',
        metadata: eventData
      }).then(({ error }) => {
        if (error) logger.error('Error logging loop blocked to DB:', error);
      });

      return;
    }

    // Normalize: lowercase, trim, remove trailing slash (except for root)
    let normalized = pathname.trim().toLowerCase();
    
    // Remove duplicate slashes
    normalized = normalized.replace(/\/+/g, '/');

    // Remove trailing slash if it exists and it's not the root
    if (normalized !== '/' && normalized.endsWith('/')) {
      normalized = normalized.slice(0, -1);
    }

    if (normalized !== pathname) {
      logger.info(`[Navigation] Normalizing URL from ${pathname} to ${normalized}`);
      
      // Update loop data
      try {
        sessionStorage.setItem('nav_redirect_loop_check', JSON.stringify({
          count: (now - loopData.last < 10000) ? loopData.count + 1 : 1,
          last: now
        }));
      } catch (e) {}

      const normEvent = {
        action: 'global_url_normalization',
        category: 'navigation',
        label: `${pathname}_to_${normalized}`,
        original_pathname: pathname
      };

      trackEvent(normEvent);

      // Log to database for the dashboard
      supabase.from('portal_analytics_events').insert({
        event_type: 'url_normalization',
        page_path: normalized,
        anonymous_id: cookieUtils.get('scooterp_anon_id') || 'unknown',
        metadata: normEvent
      }).then(({ error }) => {
        if (error) logger.error('Error logging normalization to DB:', error);
      });

      navigate(normalized + search + hash, { replace: true });
    } else {
      // If we landed on a stable URL, reset the counter if enough time has passed
      if (now - loopData.last > 5000) {
        try {
          sessionStorage.setItem('nav_redirect_loop_check', JSON.stringify({ count: 0, last: 0 }));
        } catch (e) {}
      }
    }
  }, [location.pathname, navigate]);

  // 1. Performance Measurements (Web Vitals)
  useEffect(() => {
    if (typeof window !== "undefined" && "PerformanceObserver" in window) {
      try {
        const observer = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            if (entry.entryType === "largest-contentful-paint") {
              logger.debug(`[Performance] LCP: ${entry.startTime.toFixed(2)}ms`);
            }
            if (entry.entryType === "layout-shift") {
              logger.debug(`[Performance] CLS: ${(entry as any).value.toFixed(4)}`);
            }
            if (entry.entryType === "first-input") {
              logger.debug(`[Performance] FID: ${entry.duration.toFixed(2)}ms`);
            }
          }
        });

        observer.observe({ type: "largest-contentful-paint", buffered: true });
        observer.observe({ type: "layout-shift", buffered: true });
        observer.observe({ type: "first-input", buffered: true });

        const paintEntries = performance.getEntriesByType("paint");
        paintEntries.forEach((entry) => {
          logger.debug(`[Performance] ${entry.name}: ${entry.startTime.toFixed(2)}ms`);
        });

        return () => observer.disconnect();
      } catch (e) {
        logger.warn("PerformanceObserver not supported for all types", e);
      }
    }
  }, []);

  // 1.5 Global Error Tracking (to find 404s)
  useEffect(() => {
    const handleCapture = (e: Event) => {
      const target = e.target as any;
      if (target && (target.tagName === 'IMG' || target.tagName === 'SCRIPT' || target.tagName === 'LINK')) {
        // If it's a script or link, it might be a 404
        // Check if it's already been logged to avoid spam
        const url = target.src || target.href;
          if (url) {
            logger.warn(`[ResourceError] Failed to load: ${url}`);
            
            trackEvent({
              action: 'resource_load_error',
              category: 'error',
              label: url,
              tag: target.tagName
            });
          }
      }
    };
    window.addEventListener('error', handleCapture, true);
    return () => window.removeEventListener('error', handleCapture, true);
  }, []);


  // Track navigation duration
  useEffect(() => {
    const start = performance.now();
    
    // When location changes, we can assume the previous page finished its lifecycle
      return () => {
        const end = performance.now();
        logger.debug(`[Performance] View duration for ${location.pathname}: ${(end - start).toFixed(2)}ms`);
      };
  }, [location]);

  // 2. Prefetch Strategy
  useEffect(() => {
    const prefetchCache = new Set<string>();

    const prefetchLink = (url: string) => {
      if (prefetchCache.has(url)) return;
      
      // Only prefetch internal links
      if (!url.startsWith(window.location.origin) && url.startsWith('http')) return;
      
      const path = url.replace(window.location.origin, '');
      if (!path || path === location.pathname) return;

      // Add to head as link rel=prefetch
      const link = document.createElement('link');
      link.rel = 'prefetch';
      link.href = url;
      // We can also try to find the chunk associated with this route if we had a manifest
      document.head.appendChild(link);
      prefetchCache.add(url);
      
      logger.debug(`[Prefetch] Triggered for: ${path}`);
    };

    const handleMouseOver = (e: MouseEvent) => {
      const anchor = (e.target as HTMLElement).closest('a');
      if (anchor && anchor.href) {
        prefetchLink(anchor.href);
      }
    };

    // Observer for links entering viewport
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const anchor = entry.target as HTMLAnchorElement;
          if (anchor.href) {
            prefetchLink(anchor.href);
            observer.unobserve(anchor);
          }
        }
      });
    }, { rootMargin: '50px' });

    // Initial scan and setup delegation
    document.addEventListener('mouseover', handleMouseOver);
    
    const scanLinks = () => {
      const links = document.querySelectorAll('a[href^="/"], a[href^="' + window.location.origin + '"]');
      links.forEach(link => {
        if (!prefetchCache.has((link as HTMLAnchorElement).href)) {
          observer.observe(link);
        }
      });
    };

    // Scan periodically or on DOM changes
    const mutationObserver = new MutationObserver(scanLinks);
    mutationObserver.observe(document.body, { childList: true, subtree: true });
    
    scanLinks();

    return () => {
      document.removeEventListener('mouseover', handleMouseOver);
      observer.disconnect();
      mutationObserver.disconnect();
    };
  }, [location.pathname]);

  // 3. Request Cancellation
  useEffect(() => {
    // This is more about logic in data fetching hooks, but we can log that we are ready
    // Most @tanstack/react-query hooks already handle cancellation on unmount.
    return () => {
      // Logic for canceling pending navigations if using a custom router
    };
  }, [location]);

  return null;
};
